import { useEffect, useRef, useState, type ChangeEvent, type KeyboardEvent } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Divider, FormControl, IconButton, Paper, Stack, TextField } from "@mui/material";
import Autocomplete from "@components/Autocomplete/Autocomplete";
import Button from "@components/Button/Button";
import DatePicker from "@components/DatePicker/DatePicker";
import Select from "@components/Select/Select";
import { CancelButton } from "@components/TableFilters/FiltersPanel/FiltersPanel.styles";
import type {
  DateRangeValue,
  FilterFieldDefinition,
  FilterItem,
  FiltersPanelProps,
} from "@components/TableFilters/FiltersPanel/FiltersPanel.types.ts";
import { formatOnChangeEmiratesIDNumber } from "@features/port-security/components/utils";
import { useTranslation } from "react-i18next";

const FiltersPanel = ({
  availableFields,
  filters,
  onApplyFilters,
  onCancel,
  onClearFilters,
  onLayoutChange,
}: FiltersPanelProps) => {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement | null>(null);

  const sanitizeNumberValue = (rawValue: string) => {
    const onlyNumbersAndDots = rawValue.replace(/[^0-9.]/g, "");
    const [rawIntegerPart = "", ...rest] = onlyNumbersAndDots.split(".");
    const integerPart = rawIntegerPart;
    const decimalPart = rest.join("");

    return rest.length === 0 ? integerPart : `${integerPart}.${decimalPart}`;
  };

  const sanitizeIntegerValue = (rawValue: string) => rawValue.replace(/[^0-9]/g, "");

  useEffect(() => {
    if (!panelRef.current || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => {
      onLayoutChange?.();
    });

    observer.observe(panelRef.current);

    return () => observer.disconnect();
  }, [onLayoutChange]);

  const isAllowedNumberKey = (event: KeyboardEvent<HTMLElement>) => {
    const navigationKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "Home",
      "End",
      "Enter",
    ];

    if (navigationKeys.includes(event.key)) return true;
    if (event.ctrlKey || event.metaKey) return true;
    return /^[0-9.]$/.test(event.key);
  };

  const isAllowedIntegerKey = (event: KeyboardEvent<HTMLElement>) => {
    const navigationKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "Home",
      "End",
      "Enter",
    ];

    if (navigationKeys.includes(event.key)) return true;
    if (event.ctrlKey || event.metaKey) return true;
    return /^[0-9]$/.test(event.key);
  };

  const [localFilters, setLocalFilters] = useState<FilterItem[]>(
    filters.length > 0 ? filters : [{ field: "", value: "" }],
  );

  const [touched, setTouched] = useState<boolean[]>(
    (filters.length > 0 ? filters : [{ field: "", value: "" }]).map(() => false),
  );
  const [triedApply, setTriedApply] = useState(false);
  const [lastChangedRangeKey, setLastChangedRangeKey] = useState<Array<"from" | "to" | null>>(
    (filters.length > 0 ? filters : [{ field: "", value: "" }]).map(() => null),
  );
  const valueInputRefs = useRef<Array<HTMLInputElement | HTMLTextAreaElement | null>>([]);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);

  useEffect(() => {
    if (focusIndex === null) return;

    const el = valueInputRefs.current[focusIndex];
    if (el) {
      el.focus();
    }

    setFocusIndex(null);
  }, [focusIndex]);

  const setRowTouched = (index: number, val = true) =>
    setTouched((prev) => {
      const next = [...prev];
      next[index] = val;
      return next;
    });

  const handleFieldChange = (index: number, field: string) => {
    const updated = [...localFilters];
    updated[index].field = field;
    updated[index].value = "";
    setLocalFilters(updated);
    setRowTouched(index, false);
    setLastChangedRangeKey((prev) => {
      const next = [...prev];
      next[index] = null;
      return next;
    });
    setFocusIndex(index);
  };
  const handleValueChange = (index: number, value: string) => {
    setLocalFilters((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], value };
      return next;
    });
  };

  const applyEmiratesIdMask = (value: string) => {
    const event = {
      target: { value },
    } as unknown as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
    formatOnChangeEmiratesIDNumber(event);
    return event.target.value;
  };

  const formatDateOnly = (date: Date) => {
    const year = String(date.getFullYear()).padStart(4, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDateOnlyEndOfDay = (date: Date) => `${formatDateOnly(date)}T23:59:59.999`;

  const formatRangeDateValue = (date: Date, rangeKey: "from" | "to", withTime: boolean) => {
    if (withTime) return date.toISOString();
    return rangeKey === "to" ? formatDateOnlyEndOfDay(date) : formatDateOnly(date);
  };

  const handleDateRangeChange = (
    index: number,
    rangeKey: "from" | "to",
    date: Date | null,
    withTime = true,
  ) => {
    setLocalFilters((prev) => {
      const next = [...prev];
      const current = next[index].value;
      const range: DateRangeValue =
        current && typeof current === "object" ? current : { from: "", to: "" };

      next[index] = {
        ...next[index],
        value: {
          ...range,
          [rangeKey]: date ? formatRangeDateValue(date, rangeKey, withTime) : "",
        },
      };

      return next;
    });
  };

  const handleNumberRangeChange = (index: number, rangeKey: "from" | "to", rawValue: string) => {
    setLocalFilters((prev) => {
      const next = [...prev];
      const current = next[index].value;
      const range = current && typeof current === "object" ? current : { from: "", to: "" };

      next[index] = {
        ...next[index],
        value: {
          ...range,
          [rangeKey]: sanitizeNumberValue(rawValue),
        },
      };

      return next;
    });

    setLastChangedRangeKey((prev) => {
      const next = [...prev];
      next[index] = rangeKey;
      return next;
    });
  };
  const handleAddFilter = () => {
    setLocalFilters([...localFilters, { field: "", value: "" }]);
    setTouched((prev) => [...prev, false]);
    setLastChangedRangeKey((prev) => [...prev, null]);
  };

  const handleRemoveFilter = (index: number) => {
    setLocalFilters(localFilters.filter((_, i) => i !== index));
    setTouched(touched.filter((_, i) => i !== index));
    setLastChangedRangeKey(lastChangedRangeKey.filter((_, i) => i !== index));

    valueInputRefs.current.splice(index, 1);
  };

  const validateRow = (config: FilterFieldDefinition | undefined, value: FilterItem["value"]) => {
    if (!config) return null;

    if (config.type === "text" && typeof value === "string") {
      const trimmedValue = value.trim();

      if (typeof config.minLength === "number" && trimmedValue.length < config.minLength) {
        return { type: "minLength", min: config.minLength } as const;
      }
      if (typeof config.maxLength === "number" && trimmedValue.length > config.maxLength) {
        return { type: "maxLength", max: config.maxLength } as const;
      }
    }

    if (config.type === "number" && typeof value === "string") {
      const [integerPart = "", decimalPart = ""] = value.split(".");

      if (typeof config.minLength === "number" && integerPart.length < config.minLength) {
        return { type: "minLength", min: config.minLength } as const;
      }
      if (typeof config.maxLength === "number" && integerPart.length > config.maxLength) {
        return { type: "maxLengthDigits", max: config.maxLength } as const;
      }
      if (typeof config.maxDigits === "number" && decimalPart.length > config.maxDigits) {
        return { type: "maxDigits", max: config.maxDigits } as const;
      }
    }

    if (config.type === "integer" && typeof value === "string") {
      const trimmedValue = value.trim();

      if (typeof config.minLength === "number" && trimmedValue.length < config.minLength) {
        return { type: "minLength", min: config.minLength } as const;
      }
      if (typeof config.maxLength === "number" && trimmedValue.length > config.maxLength) {
        return { type: "maxLength", max: config.maxLength } as const;
      }
    }

    if (config.type === "numberFromTo" && typeof value === "object" && value !== null) {
      const rangeValues = [value.from, value.to];

      for (const rangeValue of rangeValues) {
        if (!rangeValue) continue;

        const [integerPart = "", decimalPart = ""] = rangeValue.split(".");

        if (typeof config.minLength === "number" && integerPart.length < config.minLength) {
          return { type: "minLength", min: config.minLength } as const;
        }

        if (typeof config.maxLength === "number" && integerPart.length > config.maxLength) {
          return { type: "maxLengthDigits", max: config.maxLength } as const;
        }

        if (typeof config.maxDigits === "number" && decimalPart.length > config.maxDigits) {
          return { type: "maxDigits", max: config.maxDigits } as const;
        }
      }
      const fromValue = value.from;
      const toValue = value.to;

      if (fromValue && toValue) {
        const fromNumber = Number(fromValue);
        const toNumber = Number(toValue);

        if (!Number.isNaN(fromNumber) && !Number.isNaN(toNumber) && fromNumber > toNumber) {
          return { type: "invalidNumberRange" } as const;
        }
      }
    }

    return null;
  };

  const getValidationHelperText = (
    error: ReturnType<typeof validateRow>,
    config: FilterFieldDefinition,
  ) => {
    if (!error) return undefined;

    switch (error.type) {
      case "minLength":
        return t(
          error.min === 1 ? "form-validation.min-character" : "form-validation.min-characters",
          { min: error.min },
        );
      case "maxLength":
        return t("form-validation.max-characters", { max: error.max });
      case "maxLengthDigits":
        return t("form-validation.max-length-digits", {
          label: config.label,
          maxDigits: error.max,
        });
      case "maxDigits":
        return t("form-validation.max-length-decimals", {
          label: config.label,
          maxDecimals: error.max,
        });
      default:
        return undefined;
    }
  };
  const getNumberRangeValidationHelperText = (
    error: ReturnType<typeof validateRow>,
    rangeKey: "from" | "to",
    config: FilterFieldDefinition,
  ) => {
    if (!error) return undefined;

    if (error.type === "invalidNumberRange") {
      return rangeKey === "from"
        ? t("form-validation.from-cannot-be-greater-than-to")
        : t("form-validation.to-cannot-be-less-than-from");
    }

    return getValidationHelperText(error, config);
  };

  const handleApply = () => {
    setTriedApply(true);

    const hasError = localFilters.some((f) => {
      const conf = availableFields[f.field];
      return !!validateRow(conf, f.value);
    });

    if (hasError) return;

    const sanitizedFilters = localFilters
      .map((filter) => ({
        ...filter,
        value: typeof filter.value === "string" ? filter.value.trim() : filter.value,
      }))
      .filter((filter) => {
        if (typeof filter.value === "string") {
          return Boolean(filter.value);
        } else if (
          typeof filter.value === "object" &&
          filter.value !== null &&
          "from" in filter.value &&
          "to" in filter.value
        ) {
          return Boolean(filter.value.from || filter.value.to);
        }

        return Boolean(filter.value);
      });

    onApplyFilters(sanitizedFilters);
  };

  const handleClear = () => {
    setLocalFilters([]);
    setTouched([]);
    setTriedApply(false);
    valueInputRefs.current = [];
    onClearFilters?.();
  };

  const getInputRef = (index: number, el: HTMLInputElement | null) => {
    valueInputRefs.current[index] = el;
  };

  const renderField = (
    config: FilterFieldDefinition | undefined,
    filter: FilterItem,
    index: number,
  ) => {
    if (!config) return null;

    switch (config.type) {
      case "select":
        return (
          <Select
            testId={`value-${index}`}
            value={typeof filter.value === "string" ? filter.value : ""}
            onChange={(val) => handleValueChange(index, val)}
            options={
              config.options?.map((opt) =>
                typeof opt === "string" ? { value: opt, label: opt } : opt,
              ) ?? []
            }
            placeholder={t("common.value")}
            inputRef={(el) => getInputRef(index, el)}
          />
        );

      case "autocomplete":
        return (
          <Autocomplete
            testId={`value-${index}`}
            value={
              (config.options as Array<{ value: string; label: string }> | undefined)?.find(
                (opt) => opt.value === filter.value,
              ) || null
            }
            options={(config.options as Array<{ value: string; label: string }>) ?? []}
            onChange={(val) => handleValueChange(index, val ? (val.value as string) : "")}
            placeholder={t("common.value")}
            maxHeight={140}
            inputRef={(el) => getInputRef(index, el)}
          />
        );

      case "date":
        return (
          <TextField
            fullWidth
            type="date"
            value={typeof filter.value === "string" ? filter.value : ""}
            onChange={(e) => handleValueChange(index, e.target.value)}
            onBlur={() => setRowTouched(index)}
            inputRef={(el) => getInputRef(index, el)}
          />
        );

      case "dateFromTo":
      case "dateFromToWithoutTime": {
        const isDateFromToWithTime = config.type === "dateFromTo";
        const range: DateRangeValue =
          filter.value && typeof filter.value === "object"
            ? (filter.value as DateRangeValue)
            : { from: "", to: "" };

        const fromDate = range.from ? new Date(range.from) : null;
        const toDate = range.to ? new Date(range.to) : null;
        const dateConstraint = (config as any).dateConstraint;

        return (
          <Stack direction="row" spacing={1}>
            <DatePicker
              value={fromDate}
              onChange={(date) => handleDateRangeChange(index, "from", date, isDateFromToWithTime)}
              onClose={() => setRowTouched(index)}
              timePicker={isDateFromToWithTime}
              combinedInput
              testId={`value-${index}-from`}
              dateConstraint={dateConstraint}
            />
            <DatePicker
              value={toDate}
              onChange={(date) => handleDateRangeChange(index, "to", date, isDateFromToWithTime)}
              onClose={() => setRowTouched(index)}
              timePicker={isDateFromToWithTime}
              combinedInput
              testId={`value-${index}-to`}
              dateConstraint={dateConstraint}
            />
          </Stack>
        );
      }
      case "numberFromTo": {
        const range =
          filter.value && typeof filter.value === "object"
            ? (filter.value as { from?: string; to?: string })
            : { from: "", to: "" };

        const fromErr = validateRow(config, { from: range.from });
        const toErr = validateRow(config, { to: range.to });
        const rangeErr = validateRow(config, range);
        const lastChangedKey = lastChangedRangeKey[index];

        const finalFromErr =
          fromErr ??
          (lastChangedKey === "from" && rangeErr?.type === "invalidNumberRange" ? rangeErr : null);

        const finalToErr =
          toErr ??
          (lastChangedKey === "to" && rangeErr?.type === "invalidNumberRange" ? rangeErr : null);

        const shouldShowFromError = (touched[index] || triedApply) && !!finalFromErr;
        const shouldShowToError = (touched[index] || triedApply) && !!finalToErr;
        return (
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              placeholder={t("common.from")}
              error={shouldShowFromError}
              helperText={
                shouldShowFromError
                  ? getNumberRangeValidationHelperText(finalFromErr, "from", {
                      ...config,
                      label: t("common.from"),
                    })
                  : undefined
              }
              value={range.from}
              onChange={(e) => handleNumberRangeChange(index, "from", e.target.value)}
              onKeyDown={(e) => {
                if (!isAllowedNumberKey(e)) e.preventDefault();
              }}
              onPaste={(e) => {
                const pastedText = e.clipboardData.getData("text");
                e.preventDefault();
                handleNumberRangeChange(index, "from", pastedText);
              }}
              onBlur={() => setRowTouched(index)}
              inputRef={(el) => getInputRef(index, el)}
              slotProps={{
                htmlInput: {
                  inputMode: "decimal",
                  pattern: "[0-9.]*",
                },
              }}
            />

            <TextField
              fullWidth
              placeholder={t("common.to")}
              error={shouldShowToError}
              helperText={
                shouldShowToError
                  ? getNumberRangeValidationHelperText(finalToErr, "to", {
                      ...config,
                      label: t("common.to"),
                    })
                  : undefined
              }
              value={range.to}
              onChange={(e) => handleNumberRangeChange(index, "to", e.target.value)}
              onKeyDown={(e) => {
                if (!isAllowedNumberKey(e)) e.preventDefault();
              }}
              onPaste={(e) => {
                const pastedText = e.clipboardData.getData("text");
                e.preventDefault();
                handleNumberRangeChange(index, "to", pastedText);
              }}
              onBlur={() => setRowTouched(index)}
              slotProps={{
                htmlInput: {
                  inputMode: "decimal",
                  pattern: "[0-9.]*",
                },
              }}
            />
          </Stack>
        );
      }

      case "integer": {
        const value = typeof filter.value === "string" ? filter.value : "";
        const err = validateRow(config, value);
        const shouldShowError = (touched[index] || triedApply) && !!err;

        return (
          <TextField
            fullWidth
            placeholder={t("common.value")}
            value={value}
            onChange={(e) => handleValueChange(index, sanitizeIntegerValue(e.target.value))}
            onKeyDown={(e) => {
              if (!isAllowedIntegerKey(e)) e.preventDefault();
            }}
            onPaste={(e) => {
              const pastedText = e.clipboardData.getData("text");
              e.preventDefault();
              handleValueChange(index, sanitizeIntegerValue(pastedText));
            }}
            onBlur={() => setRowTouched(index)}
            error={shouldShowError}
            helperText={shouldShowError ? getValidationHelperText(err, config) : undefined}
            inputRef={(el) => getInputRef(index, el)}
            slotProps={{
              htmlInput: {
                inputMode: "numeric",
                pattern: "[0-9]*",
              },
            }}
            sx={{
              ".MuiFormHelperText-root": {
                marginLeft: 0,
              },
            }}
          />
        );
      }

      case "text":
      case "number":
      default: {
        const value = typeof filter.value === "string" ? filter.value : "";
        const err = validateRow(config, value);
        const shouldShowError = (touched[index] || triedApply) && !!err;

        return (
          <TextField
            disabled={config.type !== "text" && config.type !== "number"}
            fullWidth
            placeholder={t("common.value")}
            value={value}
            onChange={(e) => {
              const valueToSet =
                config.type === "number"
                  ? sanitizeNumberValue(e.target.value)
                  : filter.field === "emiratesIdNumber"
                    ? applyEmiratesIdMask(e.target.value)
                    : filter.field === "passportNumber"
                      ? e.target.value.toUpperCase()
                      : e.target.value;
              const input = (e as React.ChangeEvent<HTMLInputElement>).currentTarget;
              const selectionStart = input.selectionStart;
              const selectionEnd = input.selectionEnd;

              handleValueChange(index, valueToSet);

              if (
                e.target.value !== valueToSet &&
                selectionStart !== null &&
                selectionEnd !== null
              ) {
                input.value = valueToSet;
                input.setSelectionRange(selectionStart, selectionEnd);
              }
            }}
            onKeyDown={(e) => {
              if (config.type !== "number") return;
              if (!isAllowedNumberKey(e)) e.preventDefault();
            }}
            onPaste={(e) => {
              if (config.type !== "number" && filter.field !== "emiratesIdNumber") return;
              const pastedText = e.clipboardData.getData("text");
              e.preventDefault();
              handleValueChange(
                index,
                filter.field === "emiratesIdNumber"
                  ? applyEmiratesIdMask(pastedText)
                  : sanitizeNumberValue(pastedText),
              );
            }}
            onBlur={() => setRowTouched(index)}
            error={shouldShowError}
            helperText={shouldShowError ? getValidationHelperText(err, config) : undefined}
            inputRef={(el) => getInputRef(index, el)}
            slotProps={
              config.type === "number"
                ? {
                    htmlInput: {
                      inputMode: "decimal",
                      pattern: "[0-9.]*",
                    },
                  }
                : undefined
            }
            sx={{
              ".MuiFormHelperText-root": {
                marginLeft: 0,
              },
            }}
          />
        );
      }
    }
  };

  const hasDateFromToFilter = localFilters.some(
    ({ field }) =>
      availableFields[field]?.type === "dateFromTo" ||
      availableFields[field]?.type === "dateFromToWithoutTime",
  );

  return (
    <div ref={panelRef}>
      <Paper
        elevation={1}
        sx={{
          p: 3,
          width: hasDateFromToFilter ? 1020 : 560,
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "60vh",
          borderRadius: 2,
          overflowY: "auto",
          right: hasDateFromToFilter ? 16 : 0,
        }}
        data-testid="table-filters-content"
      >
        <Stack spacing={2}>
          {localFilters.map((filter, index) => {
            const config = availableFields[filter.field];
            return (
              <Stack key={index} direction="row" spacing={2} alignItems="center">
                <FormControl sx={{ width: "50%", alignSelf: "start" }}>
                  <Select
                    testId={`field-${index}`}
                    value={filter.field}
                    onChange={(val) => handleFieldChange(index, val)}
                    options={Object.entries(availableFields).map(([key, def]) => ({
                      value: key,
                      label: def.label,
                      testId: `${key}-filter`,
                    }))}
                    placeholder="Filter by"
                  />
                </FormControl>

                <FormControl sx={{ width: "50%" }}>
                  {renderField(config, filter, index)}
                </FormControl>

                <IconButton onClick={() => handleRemoveFilter(index)} sx={{ alignSelf: "start" }}>
                  <DeleteOutlineIcon className="text-black" />
                </IconButton>
              </Stack>
            );
          })}

          <Stack direction="row" justifyContent="flex-start">
            <Button
              testId="filter-add-button"
              variant="text"
              startIcon={<AddCircleOutlineIcon />}
              onClick={handleAddFilter}
              label={<span className="normal-case font-medium">{t("common.add-a-filter")}</span>}
            />
          </Stack>

          <Divider />

          <Stack direction="row" spacing={2} justifyContent="space-between">
            <Button
              testId="filter-clear-button"
              variant="outlined"
              onClick={handleClear}
              label={<span className="normal-case font-medium">{t("common.clear-filters")}</span>}
            />
            <div>
              <CancelButton
                testId="filter-cancel-button"
                variant="text"
                onClick={onCancel}
                label={
                  <span className="normal-case font-medium text-black">{t("common.cancel")}</span>
                }
              />
              <Button
                testId="filter-apply-button"
                variant="outlined"
                onClick={handleApply}
                label={<span className="normal-case font-medium">{t("common.apply")}</span>}
              />
            </div>
          </Stack>
        </Stack>
      </Paper>
    </div>
  );
};

export default FiltersPanel;
